import type { Question } from "./questions/question";

/** Timings copied from the prototype, in milliseconds. */
export const COUNTDOWN_FROM = 3;
export const COUNTDOWN_STEP_MS = 800;
export const RIGHT_FEEDBACK_MS = 180;
export const WRONG_FEEDBACK_MS = 1100;
export const MAX_INPUT_DIGITS = 3;
export const LOW_TIME_SECONDS = 10;
/** In a trial, Stop throws the run away only this early; after that it ends and counts. */
export const STOP_GRACE_SECONDS = 10;

export type Feedback =
  | { kind: "right" }
  | { kind: "wrong"; question: Question }
  /** The child tapped Pass: the answer is shown, without the shake. */
  | { kind: "passed"; question: Question };

/** One answered question, kept for mastery, missed facts and the dashboard. */
export interface Answer {
  typeId: string;
  factKey: string;
  correct: boolean;
  /** Time from the question appearing to Go, if known. */
  ms: number | null;
  /** The child tapped Pass instead of answering. Counts as tried and not correct. */
  passed?: boolean;
}

export type RoundState =
  | { phase: "countdown"; count: number }
  | {
      phase: "live" | "feedback";
      question: Question;
      input: string;
      score: number;
      tried: number;
      /** When the clock runs out, or null for an untimed round. */
      endAt: number | null;
      /** When the round went live. */
      startedAt: number;
      /** When the current question appeared, if known. */
      shownAt: number | null;
      feedback: Feedback | null;
      log: Answer[];
    }
  | { phase: "finished" | "abandoned"; score: number; tried: number; log: Answer[] };

export type Key = "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "⌫" | "Go";

export type RoundAction =
  | { type: "countdown-tick" }
  /** Countdown reached zero: the round goes live at `now`. */
  | { type: "begin"; now: number }
  | { type: "press"; key: Key; now?: number }
  | { type: "feedback-done"; now?: number }
  | { type: "tick"; now: number }
  /** The child tapped Done: end the round and keep what they did (Practice). */
  | { type: "finish" }
  /** The child tapped Stop: end the round and throw it away (Trials). */
  | { type: "stop" }
  /** More time on the clock (a battle move). Nothing in an untimed round. */
  | { type: "extend"; ms: number }
  /** Move on to the next question without answering this one (a battle move). */
  | { type: "skip"; now: number }
  /** The child tapped Pass: the question counts as tried and wrong, and the answer is shown. */
  | { type: "pass"; now?: number };

export interface RoundContext {
  /** The next question, or null when the round should end (e.g. placement is done). */
  next: (previous: Question | null, log: readonly Answer[]) => Question | null;
  /** Time limit, or null for untimed. */
  seconds: number | null;
}

export function initialRound(countdown = COUNTDOWN_FROM): RoundState {
  return { phase: "countdown", count: countdown };
}

/** Whole seconds left, or null for an untimed round. */
export function secondsLeft(state: RoundState, now: number): number | null {
  if (state.phase !== "live" && state.phase !== "feedback") return null;
  if (state.endAt === null) return null;
  return Math.ceil(Math.max(0, state.endAt - now) / 1000);
}

export function formatClock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * A round is a small state machine: countdown → live ⇄ feedback → finished,
 * or → abandoned if the child taps Stop. Every transition is pure; the UI
 * owns the clocks and dispatches ticks.
 */
export function createRoundReducer(ctx: RoundContext) {
  const end = (
    phase: "finished" | "abandoned",
    s: { score: number; tried: number; log: Answer[] },
  ): RoundState => ({ phase, score: s.score, tried: s.tried, log: s.log });

  return function roundReducer(state: RoundState, action: RoundAction): RoundState {
    switch (action.type) {
      case "countdown-tick":
        if (state.phase !== "countdown") return state;
        return { ...state, count: Math.max(0, state.count - 1) };

      case "begin": {
        if (state.phase !== "countdown") return state;
        const question = ctx.next(null, []);
        if (!question) return end("finished", { score: 0, tried: 0, log: [] });
        return {
          phase: "live",
          question,
          input: "",
          score: 0,
          tried: 0,
          endAt: ctx.seconds === null ? null : action.now + ctx.seconds * 1000,
          startedAt: action.now,
          shownAt: action.now,
          feedback: null,
          log: [],
        };
      }

      case "press": {
        if (state.phase !== "live") return state;
        if (action.key === "⌫") return { ...state, input: state.input.slice(0, -1) };
        if (action.key === "Go") {
          if (state.input === "") return state;
          const q = state.question;
          const correct = Number(state.input) === q.answer;
          const ms =
            action.now !== undefined && state.shownAt !== null
              ? Math.max(0, Math.round(action.now - state.shownAt))
              : null;
          const log = [...state.log, { typeId: q.typeId, factKey: q.factKey, correct, ms }];
          return {
            ...state,
            phase: "feedback",
            tried: state.tried + 1,
            score: state.score + (correct ? 1 : 0),
            feedback: correct ? { kind: "right" } : { kind: "wrong", question: q },
            log,
          };
        }
        if (state.input.length >= MAX_INPUT_DIGITS) return state;
        return { ...state, input: state.input + action.key };
      }

      case "feedback-done": {
        if (state.phase !== "feedback") return state;
        const question = ctx.next(state.question, state.log);
        if (!question) return end("finished", state);
        return {
          ...state,
          phase: "live",
          question,
          input: "",
          feedback: null,
          shownAt: action.now ?? null,
        };
      }

      case "tick":
        if (state.phase !== "live" && state.phase !== "feedback") return state;
        if (state.endAt !== null && action.now >= state.endAt) return end("finished", state);
        return state;

      case "finish":
        if (state.phase === "countdown") return end("abandoned", { score: 0, tried: 0, log: [] });
        if (state.phase !== "live" && state.phase !== "feedback") return state;
        return end("finished", state);

      case "stop":
        if (state.phase === "finished" || state.phase === "abandoned") return state;
        if (state.phase === "countdown") return end("abandoned", { score: 0, tried: 0, log: [] });
        return end("abandoned", state);

      case "extend":
        if (state.phase !== "live" && state.phase !== "feedback") return state;
        if (state.endAt === null) return state;
        return { ...state, endAt: state.endAt + action.ms };

      case "skip": {
        if (state.phase !== "live") return state;
        const question = ctx.next(state.question, state.log);
        if (!question) return end("finished", state);
        return { ...state, question, input: "", shownAt: action.now };
      }

      case "pass": {
        if (state.phase !== "live") return state;
        const q = state.question;
        const ms =
          action.now !== undefined && state.shownAt !== null
            ? Math.max(0, Math.round(action.now - state.shownAt))
            : null;
        return {
          ...state,
          phase: "feedback",
          input: "",
          tried: state.tried + 1,
          feedback: { kind: "passed", question: q },
          log: [
            ...state.log,
            { typeId: q.typeId, factKey: q.factKey, correct: false, ms, passed: true },
          ],
        };
      }
    }
  };
}

/** Whether Stop may still throw a trial away: during the countdown or its first few seconds. */
export function canDiscard(state: RoundState, now: number): boolean {
  if (state.phase === "countdown") return true;
  if (state.phase !== "live" && state.phase !== "feedback") return false;
  return now - state.startedAt < STOP_GRACE_SECONDS * 1000;
}

/**
 * Where an even pace to the pass mark would be by now: pass mark × time gone
 * ÷ time limit. The same for everyone, so showing it keeps the trial honest.
 */
export function paceTarget(passMark: number, seconds: number, secondsLeft: number): number {
  if (seconds <= 0) return passMark;
  const gone = Math.min(seconds, Math.max(0, seconds - secondsLeft));
  return (passMark * gone) / seconds;
}

/** Ahead of (or level with) an even pace to the pass mark. */
export function onTrack(score: number, passMark: number, seconds: number, left: number): boolean {
  return score >= Math.floor(paceTarget(passMark, seconds, left));
}
